import axios, { AxiosResponse } from 'axios';
import i18n from '~/i18n';
import {
    CourseraCategory,
    CourseraCheckHasAccessResponse,
    CourseraCollection,
    CourseraCourseDetail,
    CourseraDifficultyLevel,
    CourseraProgram,
    CourseraSearchResponse,
    CourseraUseAccessCodeResponse,
    GetProgramResponse,
    GetProgramResponseContent,
} from '~/models';
import { StripeSession } from './BillingService';

function isCourseraDifficultyLevel(level: string): level is CourseraDifficultyLevel {
    return ['BEGINNER', 'INTERMEDIATE', 'ADVANCED'].includes(level);
}

export default class CourseraService {
    private static getUrl(path: string, params?: Record<string, unknown>) {
        let query = '';
        if (!!params && !!Object.keys(params).length) {
            const convertedParams = Object.entries(params).reduce<Record<string, string>>(
                (acc, [key, value]) => {
                    if (value === undefined) return acc;
                    return {
                        ...acc,
                        [key]: value + '',
                    };
                },
                {}
            );

            const usp = new URLSearchParams(convertedParams);
            query = '?' + usp.toString();
        }

        return `${process.env.VUE_APP_API_URL}v1/coursera/${path}${query}`;
    }

    private static getCourseDetails(content: GetProgramResponseContent): CourseraCourseDetail[] {
        const details: CourseraCourseDetail[] = [];

        const metadata = content.extraMetadata;

        if (!!metadata) {
            if (metadata.typeName == 'courseMetadata') {
                // Duration
                if (!!metadata.definition.estimatedLearningTime) {
                    const hours =
                        Math.round((metadata.definition.estimatedLearningTime / 3600) * 2) / 2;
                    let hoursString = hours.toFixed(1);
                    if (hoursString.endsWith('.0'))
                        hoursString = hoursString.substring(0, hoursString.length - 2);

                    const title = i18n
                        .t('coursera.courseModal.about.approxDuration', {
                            hours: hoursString,
                        })
                        .toString();
                    details.push({
                        type: 'duration',
                        title,
                    });
                }
            }
        }

        // Difficulty level
        if (!!content.difficultyLevel && isCourseraDifficultyLevel(content.difficultyLevel)) {
            const title = i18n
                .t(
                    `coursera.courseModal.about.difficultyLevel.${content.difficultyLevel.toLowerCase()}`
                )
                .toString();

            details.push({
                type: 'level',
                title,
            });
        }

        // Language
        if (content.languageCode === 'en')
            details.push({
                type: 'language',
                title: 'English',
            });

        return details;
    }

    static async getProgram(): Promise<CourseraProgram> {
        const res: AxiosResponse<GetProgramResponse> = await axios({
            method: 'GET',
            url: this.getUrl('program'),
        });

        const collections = res.data.collections.reduce<CourseraCollection[]>((acc, coll) => {
            if (!coll.contents.length) return acc;

            acc.push({
                ...coll,
                contents: coll.contents.map(cont => {
                    const upcomingSessions =
                        cont.extraMetadata.typeName === 'specializationMetadata'
                            ? null
                            : cont.extraMetadata.definition.upcomingSessions;

                    const nextSessionStart =
                        upcomingSessions === null || !upcomingSessions.length
                            ? null
                            : new Date(upcomingSessions[0].startsAt);

                    const nextSessionEnd =
                        upcomingSessions !== null && nextSessionStart !== null
                            ? new Date(upcomingSessions[0].endsAt)
                            : null;

                    return {
                        ...cont,
                        promoPhoto: cont.extraMetadata.definition.promoPhoto,
                        new: false,
                        details: this.getCourseDetails(cont),
                        nextSessionStart,
                        nextSessionEnd,
                    };
                }),
            });
            return acc;
        }, []);

        const program = {
            ...res.data,
            collections,
        };

        return program;
    }

    static async getCategories(): Promise<CourseraCategory[]> {
        const res: AxiosResponse<{
            categories: CourseraCategory[];
        }> = await axios({
            method: 'GET',
            url: this.getUrl('categories'),
        });

        return res.data.categories;
    }

    static async useAccessCode(code: string): Promise<CourseraUseAccessCodeResponse> {
        const res: AxiosResponse<CourseraUseAccessCodeResponse> = await axios({
            method: 'GET',
            url: this.getUrl(`code/${code}`),
        });

        return res.data;
    }

    static async checkHasAccess(): Promise<CourseraCheckHasAccessResponse> {
        const res: AxiosResponse<CourseraCheckHasAccessResponse> = await axios({
            method: 'GET',
            url: this.getUrl('access'),
        });

        return {
            ...res.data,
            expiresAt: !res.data.expiresAt ? null : new Date(res.data.expiresAt),
        };
    }

    static async searchCourses(
        q: string,
        limit?: number,
        after?: number
    ): Promise<CourseraSearchResponse> {
        const res: AxiosResponse<CourseraSearchResponse> = await axios({
            method: 'GET',
            url: this.getUrl('search', { q, limit, after }),
        });
        return res.data;
    }

    static async setupFutureSubscription(
        billingAccountId: string,
        priceId: string,
        returnUrl: string
    ) {
        const res: AxiosResponse<StripeSession> = await axios({
            method: 'POST',
            url: this.getUrl('subscription/future'),
            data: {
                billingAccountId,
                priceId,
                returnUrl,
            },
        });
        return res.data;
    }
}
